import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { Tc } from 'src/app/components/core/_infrastructure/types/common';
import { CampaniaEspecificaService } from '../../../campaniasespecificas/campaniaespecifica.service';
import { TCampaniaEspecificaViewModel } from '../../../campaniasespecificas/campaniaespecifica.types';
import { DocumentacionCamService } from '../documentacioncam.service';
import { TwDocumentacionCamViewModel } from '../documentacioncam.types';
import { combineLatest } from 'rxjs';
import { NavMenuService } from '../../../../../core/components/navmenu/navmenu.service';

@Component({
    selector: 'app-documentacioncam-wrapper',
    templateUrl: './documentacioncam-wrapper.component.html',
})
export class DocumentacionCamWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinner = false;

    campaniaEspecifica: TCampaniaEspecificaViewModel;
    documentos: TwDocumentacionCamViewModel[];

    formGroup: FormGroup;

    tiposDocumentacionCam: Tc[];

    vm$ = combineLatest([this.documentacionCamService.getTipoDocumentacionCam(), this.activatedRoute.paramMap]).pipe(
        map(([items, params]) => {
            this.tiposDocumentacionCam = items;
            const idCampania: any = params.get('idCampania');
            if (idCampania) {
                this.getCampaniaById(idCampania);
            }
            return {};
        }),
    );

    private messageErrorAlreadyExist = 'Ya existe esta Documentación para la Campaña';
    private messageErrorOnSave = 'Debe incluir al menos una Documentación si la Campaña admite adjuntos';
    private successMessage = 'Documentación actualizada correctamente';
    private route = '/mantenimiento/campanias';

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private documentacionCamService: DocumentacionCamService,
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
        private campaniaEspecificaService: CampaniaEspecificaService,
        private navMenuService: NavMenuService,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit() {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            basic: this.fb.group({
                //Id: [null],
                IdCampaniaDescripcion: '',
                AdjuntarDocumentacion: [false, Validators.required],
                IdTipoDocumentacion: [{ value: null, disabled: true }], // Deshabilitado al inicio, ya que 'AdjuntarDocumentacion' es false
                Obligatoria: [false],
                //Variable creada para que el formulario detecte un cambio en el Listado de Documentaciones
                DocumentoLista: false,
            }),
        });
    }

    private getCampaniaById(id: number): void {
        this.campaniaEspecificaService
            .getById(id)
            .pipe(
                tap((ce: TCampaniaEspecificaViewModel) => {
                    this.campaniaEspecifica = ce;
                    this.documentos = [...ce.Documentacion];
                    // @author: scalero1
                    // @subject: Se realiza el include de TcTipoDocumentacionCam, para que se reciba la descripción del tipo de documentación
                    // y ya no sea necesario realizar el mapeo en el front.
                    this.campaniaEspecificaService.campaniaEspecifica = this.campaniaEspecifica;
                    this.navMenuService.updateMenuServiceForCampania(ce.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    this.formGroup.disable();
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    onSave(): void {
        this.visibilitySpinner = true;

        const campania = { ...this.formGroup.value.basic };
        this.campaniaEspecifica.AdjuntarDocumentacion = campania.AdjuntarDocumentacion;
        this.campaniaEspecifica.Documentacion = this.documentos;

        if (
            this.formGroup.value.basic.AdjuntarDocumentacion == true &&
            (this.campaniaEspecifica.Documentacion.length == 0 || this.campaniaEspecifica.Documentacion.every(doc => doc.MarkToDelete == 1))
        ) {
            this.notificationService.addErrorMessage(this.messageErrorOnSave);
            this.visibilitySpinner = false;
            return;
        }

        this.campaniaEspecificaService
            .saveDocumentation(this.campaniaEspecifica)
            .pipe(
                tap(() => {
                    this.visibilitySpinner = false;
                    this.addSuccessMessage(this.successMessage);
                    this.getCampaniaById(this.campaniaEspecifica.Id!);
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.visibilitySpinner = false;
                    if (error?.message?.indexOf(this.messageErrorAlreadyExist) != -1) {
                        this.formGroup.disable();
                    }
                    return of([]);
                }),
            )
            .subscribe(); // Sin subscribe, no se ejecuta la invocación al servicio
    }

    addNewDocumentation(tipoDocObl: { IdTipoDocumentacion: number; Obligatoria: boolean }) {
        const documento: TwDocumentacionCamViewModel = new TwDocumentacionCamViewModel();

        documento.IdCampania = this.campaniaEspecifica.Id!;
        documento.IdTipoDocumentacion = tipoDocObl.IdTipoDocumentacion;
        documento.IdTipoDocumentacionDescripcion =
            this.tiposDocumentacionCam?.find(tiposDocumentacion => tiposDocumentacion.Id == documento.IdTipoDocumentacion)?.Descripcion || '';
        documento.Obligatoria = tipoDocObl.Obligatoria;
        documento.MarkToDelete = 0;

        if (
            documento.IdTipoDocumentacion == null ||
            undefined ||
            0 ||
            this.documentos.find(documentoAdd => {
                return documentoAdd.IdTipoDocumentacion === documento.IdTipoDocumentacion;
            })
        ) {
            this.notificationService.addErrorMessage(this.messageErrorAlreadyExist);
            return;
        }

        this.documentos = [...this.documentos, documento];
    }

    onDelete(documentationRow: TwDocumentacionCamViewModel) {
        let documento = this.documentos?.find(documento => documento.IdTipoDocumentacion === documentationRow.IdTipoDocumentacion);
        if (documento == null && documentationRow.Id == null) {
            let index = this.documentos.indexOf(documentationRow, 0);
            if (index == -1) {
                return;
            }
            this.documentos.splice(index, 1);
            return;
        }
        documento!.MarkToDelete = 1;
        this.formGroup.value.basic.DocumentoLista = true;
        this.documentos = [...this.documentos];
    }

    onUndoDelete(documentationRow: TwDocumentacionCamViewModel) {
        let documento = this.documentos?.find(documento => documento.IdTipoDocumentacion === documentationRow.IdTipoDocumentacion);
        if (documento == null) {
            return;
        }
        documento.MarkToDelete = 0;
        this.documentos = [...this.documentos];
    }

    markAllToDelete(adjuntarValue: boolean) {
        const campania: TCampaniaEspecificaViewModel = new TCampaniaEspecificaViewModel();

        campania.AdjuntarDocumentacion = adjuntarValue;

        for (let documento of this.documentos) {
            documento.MarkToDelete = !campania.AdjuntarDocumentacion ? 1 : 0;
        }

        this.documentos = [...this.documentos];
    }

    onBack(): void {
        this.router.navigate([this.route]);
    }

    get formInvalid(): boolean {
        return !this.formGroup.valid || !this.formGroup.dirty || this.formGroup.pristine;
    }
}
